import React, { useMemo, useCallback } from "react";
import Select from "../Select/Select";
import Button from "../Button/Button";
import styles from "./Multi.module.css";
import { Trash2 } from "lucide-react";

/**
 * Rows are { type, value } (no synthetic id).
 * Assumes the parent ensures at least one starter row when opening the modal.
 */
export default function Multi({
    labelLeft,
    labelRight,
    required = false,
    items,
    onChange,
    options,
    inputPlaceholder,
    inputType,
}) {
    // Normalize props with memo to satisfy exhaustive-deps
    const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);
    const allOptions = useMemo(
        () => (Array.isArray(options) ? options : []),
        [options]
    );

    const enabledOptions = useMemo(
        () => allOptions.filter((o) => !o?.disabled),
        [allOptions]
    );

    const taken = useMemo(
        () => new Set(list.map((it) => it.type).filter(Boolean)),
        [list]
    );

    const canAddMore = list.length < enabledOptions.length;

    const add = useCallback(() => {
        // pick first unused enabled option (fallback to empty string)
        const nextOpt = enabledOptions.find((o) => !taken.has(o.value));
        const nextType = nextOpt?.value ?? "";
        onChange?.([...list, { type: nextType, value: "" }]);
    }, [enabledOptions, taken, onChange, list]);

    const remove = useCallback(
        (idx) => {
            if (list.length <= 1) return; // guard; UI already disables the button
            const next = list.slice(0, idx).concat(list.slice(idx + 1));
            onChange?.(next);
        },
        [list, onChange]
    );

    const change = useCallback(
        (idx, patch) => {
            const next = list.map((it, i) => (i === idx ? { ...it, ...patch } : it));
            onChange?.(next);
        },
        [list, onChange]
    );

    return (
        <div className={styles.wrap}>
            <div className={styles.header}>
                <div className={styles.hcell}>
                    {labelLeft}
                    {required ? "*" : ""}
                </div>
                <div className={styles.hcell}>
                    {labelRight}
                    {required ? "*" : ""}
                </div>
                <div className={styles.hcellEnd} />
            </div>

            {list.map((it, idx) => {
                // For this row, allow its current selection, but hide options taken by other rows
                const filtered = allOptions.filter(
                    (o) => o?.value === it.type || !taken.has(o?.value)
                );

                // If this row has no selection yet, prepend a placeholder
                const optionsForRow =
                    it.type && it.type !== ""
                        ? filtered
                        : [{ value: "", label: "Select…" }, ...filtered];

                return (
                    <div className={styles.row} key={idx}>
                        <div className={styles.cell}>
                            <Select
                                value={it.type ?? ""}
                                onChange={(v) => change(idx, { type: v })}
                                options={optionsForRow}
                            />
                        </div>
                        <div className={styles.cell}>
                            <input
                                className={styles.input}
                                type={inputType || "text"}
                                placeholder={inputPlaceholder}
                                value={it.value ?? ""}
                                onChange={(e) => change(idx, { value: e.target.value })}
                            />
                        </div>
                        <Button
                            variant="danger"
                            shape="square"
                            leadingIcon={<Trash2 size={18} />}
                            onClick={() => remove(idx)}
                            disabled={list.length <= 1}
                            aria-disabled={list.length <= 1}
                        />
                    </div>
                );
            })}

            <Button
                variant="secondary"
                shape="pill"
                onClick={add}
                disabled={!canAddMore}
                aria-disabled={!canAddMore}
                className={styles.add}
            >
                + Add another subvalue
            </Button>
        </div>
    );
}
